import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { ManageBookingsAction, ManageBookingsExcludedOrder } from "../manageBookingsTypes"

type Props = Readonly<{
    open: boolean
    onClose: () => void
    onConfirm: () => void
    isLoading: boolean
    action: ManageBookingsAction
    affectedOrders: number
    excludedOrders: ManageBookingsExcludedOrder[]
    userName: string
    newOwnerName?: string
}>

const useStyles = makeStyles()(() => ({
    content: {
        gap: "1.5rem",
        padding: "0 4rem 2rem",
        alignItems: "stretch",
    },
    messageBlock: {
        alignItems: "flex-start",
        gap: ".25rem",
    },
    buttonsRow: {
        flexDirection: "row",
        gap: "1rem",
        justifyContent: "center",
    },
    button: {
        borderRadius: ".5rem",
        paddingLeft: "2rem",
        paddingRight: "2rem",
    },
}))

export function ManageBookingsConfirmDialog({
    open,
    onClose,
    onConfirm,
    isLoading,
    action,
    affectedOrders,
    excludedOrders,
    userName,
    newOwnerName,
}: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const isMove = action === ManageBookingsAction.CHANGE_ORGANIZER
    const hasOrders = affectedOrders > 0

    const title = isMove
        ? l10n.getString("service-center-admin-organization-manage-bookings-confirm-modal-move-title")
        : l10n.getString("service-center-admin-organization-manage-bookings-confirm-modal-delete-title")

    const renderMainMessage = () => {
        if (!hasOrders) {
            return isMove ? (
                <LocalizedStrict id="service-center-admin-organization-manage-bookings-confirm-modal-move-no-orders">
                    <Typography variant="body1">There are no orders to move.</Typography>
                </LocalizedStrict>
            ) : (
                <LocalizedStrict id="service-center-admin-organization-manage-bookings-confirm-modal-delete-no-orders">
                    <Typography variant="body1">There are no orders to delete.</Typography>
                </LocalizedStrict>
            )
        }
        return isMove ? (
            <LocalizedStrict
                id="service-center-admin-organization-manage-bookings-confirm-modal-move-message"
                vars={{ count: affectedOrders }}
            >
                <Typography variant="body1">Please confirm that you want to move {affectedOrders} orders.</Typography>
            </LocalizedStrict>
        ) : (
            <LocalizedStrict
                id="service-center-admin-organization-manage-bookings-confirm-modal-delete-message"
                vars={{ count: affectedOrders }}
            >
                <Typography variant="body1">Please confirm that you want to delete {affectedOrders} orders.</Typography>
            </LocalizedStrict>
        )
    }

    return (
        <StandardDialog open={open} onClose={onClose} dialogTitle={title} maxWidthProp="sm" hideCloseIcon>
            <Stack className={classes.content}>
                <Stack className={classes.messageBlock}>
                    {renderMainMessage()}
                    <LocalizedStrict
                        id="service-center-admin-organization-manage-bookings-confirm-modal-from"
                        vars={{ name: userName }}
                    >
                        <Typography variant="body1">From: {userName}</Typography>
                    </LocalizedStrict>
                    {isMove && newOwnerName && (
                        <LocalizedStrict
                            id="service-center-admin-organization-manage-bookings-confirm-modal-to"
                            vars={{ name: newOwnerName }}
                        >
                            <Typography variant="body1">To: {newOwnerName}</Typography>
                        </LocalizedStrict>
                    )}
                </Stack>

                {excludedOrders.length > 0 && (
                    <Stack gap=".5rem" width="100%">
                        {excludedOrders.map((item, i) => (
                            <LocalizedStrict
                                key={i}
                                id="service-center-admin-organization-manage-bookings-confirm-modal-excluded-orders"
                                vars={{ count: item.count, reason: item.reason }}
                                attrs={{ title: true }}
                            >
                                <StandardAlertInfo title="10 orders excluded:" />
                            </LocalizedStrict>
                        ))}
                    </Stack>
                )}

                <Stack className={classes.buttonsRow}>
                    {hasOrders ? (
                        <>
                            <LocalizedStrict id="service-center-admin-organization-manage-bookings-confirm-modal-cancel-button">
                                <StandardButton
                                    color="grey"
                                    variant="outlined"
                                    onClick={onClose}
                                    size="medium"
                                    fullWidth={false}
                                    updateDefaultClasses={classes.button}
                                >
                                    Cancel
                                </StandardButton>
                            </LocalizedStrict>
                            <LocalizedStrict id="service-center-admin-organization-manage-bookings-confirm-modal-submit-button">
                                <StandardLoadingButton
                                    color="black"
                                    onClick={onConfirm}
                                    loading={isLoading}
                                    size="medium"
                                    fullWidth={false}
                                    updateDefaultClasses={classes.button}
                                >
                                    Confirm
                                </StandardLoadingButton>
                            </LocalizedStrict>
                        </>
                    ) : (
                        <LocalizedStrict id="service-center-admin-organization-manage-bookings-confirm-modal-close-button">
                            <StandardButton
                                color="black"
                                onClick={onClose}
                                size="medium"
                                fullWidth={false}
                                updateDefaultClasses={classes.button}
                            >
                                Close
                            </StandardButton>
                        </LocalizedStrict>
                    )}
                </Stack>
            </Stack>
        </StandardDialog>
    )
}
